import { DeletableBadge } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import type { MentionTarget } from "./useMentions";

export interface MentionsBadgeRowProps {
  pendingMentions: MentionTarget[];
  /** IDs already in the conversation (assignedTo / userIds). Used to mark chips as "[New]". */
  existingUserIds: string[];
  onRemove: (target: MentionTarget) => void;
}

const isNewToConversation = (target: MentionTarget, existingUserIds: string[]): boolean => {
  if (target.type === "user") return !existingUserIds.includes(target.user.id);
  // A group is "new" if any of its members aren't already in the conversation
  return target.userIds.some((id) => !existingUserIds.includes(id));
};

export const MentionsBadgeRow = ({ pendingMentions, existingUserIds, onRemove }: MentionsBadgeRowProps) => {
  const { t } = useTranslation("team-chat");

  if (pendingMentions.length === 0) return null;

  const hasNewMentions = pendingMentions.some((m) => isNewToConversation(m, existingUserIds));

  return (
    <div className="px-4 pt-2 pb-1 border-t border-t-border space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">{t("Mentions")}:</span>
        {pendingMentions.map((target) => {
          const isNew = isNewToConversation(target, existingUserIds);
          const label = target.type === "user" ? formatName(target.user) : target.name;
          return (
            <DeletableBadge
              key={target.type === "user" ? target.user.id : `group:${target.name}`}
              variant={isNew ? "default" : "secondary"}
              onDelete={() => onRemove(target)}
            >
              {isNew && <span className="mr-1 opacity-70">{t("[New]")}</span>}
              {label}
            </DeletableBadge>
          );
        })}
      </div>
      {hasNewMentions && (
        <p className="text-xs text-muted-foreground">
          {t("New mentions will be added to the conversation and can read prior messages")}
        </p>
      )}
    </div>
  );
};
